import classNames from "classnames";
import React from "react";
import { MeetingStatus } from "@fidget/common/models/meeting-status";
import { L10n } from "../../i18n/index";
import { intl } from "../../i18n/TypedIntl";
import { UserProfile } from "../../models/userProfile";
import { getFriendlyDateString, Mode } from "../../utils/timeFormatter";
import { UserPresenter } from "../../utils/userPresenter";
import { BackgroundType, ChatItemBorder } from "./chatItemBorder";
import styles from "./chatMeeting.css";

interface Props {
  agent: UserProfile | undefined;
  start: number;
  duration: number;
  meetingStatus: MeetingStatus;
  changed: boolean;
  widgetStyle?: boolean;
}

/**
 * Displays box with information about a scheduled meeting in widget chat.
 * @param  {UserProfile}  props.agent    Agent profile
 * @param  {number}       props.start    start timestamp in milliseconds
 * @param  {number}       props.duration duration in milliseconds
 * @param  {MeetingStatus} props.meetingStatus current status of the meeting
 * @param  {boolean}      props.changed  whether or not the meeting has changed
 * @param  {boolean|undefined} props.widgetStyle  component style
 */
export const ChatMeeting: React.SFC<Props> = ({
  agent,
  start,
  duration,
  meetingStatus,
  changed,
  widgetStyle
}) => {
  const agentPresenter = agent && UserPresenter.from(agent);
  let title: string;
  if (!changed && meetingStatus === MeetingStatus.RESCHEDULED) {
    title = intl.formatMessage("conversation.meeting.meetingRescheduled");
  } else if (!changed && meetingStatus === MeetingStatus.CANCELLED) {
    title = intl.formatMessage("conversation.meeting.meetingCancelled");
  } else {
    title = intl.formatMessage("conversation.meeting.meetingScheduled");
  }

  return (
    <ChatItemBorder widgetStyle={widgetStyle} backgroundType={BackgroundType.AGENT}>
      <div
        className={classNames(styles.container, {
          [styles.widget]: !!widgetStyle,
          [styles.dashboard]: !widgetStyle,
          [styles.changed]: changed,
          [styles.cancelled]: meetingStatus === MeetingStatus.CANCELLED
        })}
      >
        <div className={styles.title}>{title}</div>
        <div className={styles.details}>
          <div>
            <i className="icon-cal-icon"></i>
            {meetingStatus === MeetingStatus.RESCHEDULED && (
              <strong>
                <L10n id="conversation.meeting.newDate" />
              </strong>
            )}
            <span>
              {getFriendlyDateString(start, { useToday: true, useTomorrow: true, mode: Mode.LONG })}
              {", "}
              {intl.formatTime(start)} {"-"} {intl.formatTime(start + duration)}
            </span>
          </div>
          {agentPresenter && (
            <div>
              <i className="icon-person"></i>
              <span>{agentPresenter.displayedName}</span>
            </div>
          )}
        </div>
        {changed && meetingStatus === MeetingStatus.RESCHEDULED && (
          <div className={styles["changed-info"]}>
            <L10n id={"conversation.meeting.notifyRescheduled"} />
          </div>
        )}
        {changed && meetingStatus === MeetingStatus.CANCELLED && (
          <div className={styles["changed-info"]}>
            <L10n id={"conversation.meeting.notifyCancelled"} />
          </div>
        )}
      </div>
    </ChatItemBorder>
  );
};
